import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import pluralize from "pluralize";

import { AD_LAYOUT_STUBS } from "@consts";
import { newsArticleSchema } from "@utils";
import { getCMSCopy } from "@/common/services/cmsCopyService";

dayjs.extend(utc);
dayjs.extend(tz);

export default {
	data() {
		return {
			AD_LAYOUT_STUBS,
			serverDateParam: null,
			cmsContent: {},
			scores: [],
			displayDate: "",
			centerLetter: "",
			outerLetters: [],
			pangrams: [],
			answers: [],
		};
	},
	// asyncData: STRICTLY the cache-expiry header. It is the only hook with
	// access to `res`, which is the sole reason it still exists here.
	asyncData({ res }) {
		if (!res) {
			return;
		}

		const HOUR = 3;
		const MINUTE = 6;
		const now = dayjs().tz("America/New_York");
		let nextRefreshDate = now.hour(HOUR).minute(MINUTE).startOf("minute");
		if (nextRefreshDate.isBefore(now)) {
			// Same time tomorrow
			nextRefreshDate = nextRefreshDate.add(1, "day");
		}

		res.setHeader(
			"Expires",
			nextRefreshDate.utc().format("ddd, DD MMM YYYY HH:mm:ss [GMT]")
		);
	},
	// fetch: content loading (SSR + client nav). Non-blocking on the client, so
	// navigating here no longer freezes the route transition.
	async fetch() {
		if (process.server) {
			this.serverDateParam = this.resolveDate();
		}

		await this.loadData();
	},
	// Compare-first: only refetch when the visitor's day genuinely differs from
	// the server's, so we don't flash the spinner over content that was already
	// right.
	mounted() {
		if (
			this.serverDateParam &&
			this.resolveDate() !== this.serverDateParam
		) {
			this.$fetch();
		}
	},
	head() {
		const title = (this.cmsContent.metaTitle || "").replace(
			/\{date\}/g,
			this.displayDate.split(",")[0]
		);
		return {
			title,
			meta: [
				{
					name: "description",
					hid: "description",
					content: this.cmsContent.metaDescription,
				},
			],
			script: [
				newsArticleSchema({
					headline: title,
					image: "https://wordfinder.yourdictionary.com/static/images/spelling-bee-og.jpg",
					datePublished: "2022-08-24T08:00:00.000Z",
				}),
			],
		};
	},
	methods: {
		resolveDate() {
			return dayjs().format("YYYY-MM-DD");
		},
		async loadData() {
			// NOTE: /v4/spelling_bee has no dated request shape yet — it always
			// returns "today" per the API's own clock and ignores a `date`
			// param. So the mounted() correction re-reads the rolling resource
			// rather than fetching a specific (immutable) day. Once the
			// endpoint accepts a date, pass this.resolveDate() here.
			const [serviceResponse, cmsResponse] = await Promise.all([
				this.$axios
					.get(`${this.$config.API_SERVICE}/v4/spelling_bee`)
					.then((response) =>
						response.status === 200 && response.data.status === 200
							? response.data
							: {}
					)
					.catch((err) => {
						console.error(
							"Could not connect to the spelling bee endpoint. ",
							err
						);
					}),
				getCMSCopy(
					this.$axios,
					this.$config,
					".seo.landing.nyt-spelling-bee",
					process.env.version
				),
			]);

			this.cmsContent = cmsResponse?.data || {};
			this.scores = serviceResponse?.data?.scores || [];
			this.displayDate = serviceResponse?.data?.displayDate || "";
			this.centerLetter = serviceResponse?.data?.centerLetter || "";
			this.outerLetters = serviceResponse?.data?.outerLetters || [];
			this.pangrams = serviceResponse?.data?.pangrams || [];
			this.answers = serviceResponse?.data?.answers || [];
		},
	},
	computed: {
		letters() {
			const letters = [];
			if (this.centerLetter) {
				letters.push(this.centerLetter);
			}
			if (this.outerLetters?.length) {
				letters.push(...this.outerLetters);
			}

			return letters;
		},
		sections() {
			const s = [];
			if (this.pangrams.length) {
				s.push({
					title: this.$t("SpellingBeeTodayPangrams"),
					subtitle: pluralize("word", this.pangrams.length, true),
					words: this.pangrams,
				});
			}

			if (this.answers.length) {
				const grouped = this.answers.reduce((acc, word) => {
					const l = word.length;
					acc[l] = acc[l] || [];
					acc[l].push(word);
					return acc;
				}, {});

				Object.keys(grouped)
					.sort((a, b) => a - b)
					.forEach((length) => {
						const words = grouped[length];
						if (words.length) {
							s.push({
								title: `${length}-Letter Answers`,
								subtitle: pluralize("word", words.length, true),
								words,
							});
						}
					});
			}

			return s;
		},
		geniusPoints() {
			return this.scores?.[8] || 0;
		},
		totalPoints() {
			return this.scores?.[9] || 0;
		},
	},
};
